import { FC, useEffect, useState } from 'react';
import { Loader, Page, PageTitleWithBack } from '../../../components';
import { Grid } from '@mui/material';
import { ICompiledSpecOption } from '../../../models';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import { useParams } from 'react-router-dom';
import { getSpecSelectedOptionsById } from '../../../fetch';
import { SelectedOptionsTable } from './SelectedOptionsTable';
import { useSnackbar } from 'notistack';

interface ISelectedBuildOptions {
  path?: string;
  pageTitle?: string;
}

export const SelectedBuildOptions: FC<ISelectedBuildOptions> = ({
  path,
  pageTitle = 'Selected Spec Build Options'
}) => {
  const classes = useStyles();
  const { id } = useParams<{ id: string }>();
  const { enqueueSnackbar } = useSnackbar();
  const [isFetching, setIsFetching] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState<ICompiledSpecOption[]>([]);
  const [neighborhood, setNeighborhood] = useState<string | undefined>(undefined);
  const [lotNumber, setLotNumber] = useState<string | number | undefined>(undefined);

  const fetchSelectedOptions = async () => {
    setIsFetching(true);
    try {
      const res = await getSpecSelectedOptionsById({
        buildId: id
      });
      setSelectedOptions([...res.selectedOptions, ...res.addendumAOptions]);
      setNeighborhood(res.neighborhoodName);
      setLotNumber(res.lotNumber);
    } catch (e) {
      console.log(e);
      enqueueSnackbar(`Error loading selected options, please try again.`, {
        variant: 'error'
      });
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    fetchSelectedOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Page title={pageTitle} showSideBar={false} printOrientation="landscape">
      <Grid container>
        <Grid item xs={12} className={classes.pageTitleWrapper}>
          <PageTitleWithBack
            showDivider={true}
            backText="Back to Spec Details"
            backUrl={`${path ?? '/development'}/build-options/${id}`}
            title="Selected Options"
          />
        </Grid>
      </Grid>
      {isFetching && <Loader type="inline" position="centered" />}
      {!isFetching && (
        <SelectedOptionsTable
          selectedOptions={selectedOptions}
          title={neighborhood && lotNumber ? `${neighborhood} - Lot ${lotNumber}` : undefined}
        />
      )}
    </Page>
  );
};

const useStyles = makeStyles((theme: Theme) => {
  return {
    pageTitleWrapper: {
      '@media (min-width: 768px)': {
        flex: 1
      }
    },
    print: {
      '@media print': {
        height: '100%',
        width: '100%',
        maxWidth: 'none',
        maxHeight: 'none',
        margin: 0,
        background: '#fff'
      }
    },
    picture: {
      userSelect: 'none',
      cursor: 'pointer',
      position: 'relative',
      '&:hover::before': {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        color: theme.palette.common.white,
        content: '"See Larger"',
        fontSize: '0.85rem',
        position: 'absolute',
        textAlign: 'center',
        height: 125,
        width: 125,
        padding: 4,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }
    },
    optionImage: { cursor: 'pointer', height: 125, width: 125 }
  };
});
